import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {PagedListResponse, User} from '../../../../domain';
import {
  currentTimeZone,
  Disposers,
  HttpPromiseState,
  logger,
  makeTime,
  StringControl,
  validators
} from '../../../../lib/helpers';
import {
  ClassroomRole,
  ClassroomUserFilter,
  ClassroomUserSearchDto
} from '../../../classroom/models/classroom-models';
import {ClassroomLoaders} from '../../../classroom/stores/classroom-loaders';
import {AdminClassroomSceneStore} from '../AdminClassroomSceneStore';

export class UserSearchStore {
  @observable
  expanded: boolean;

  userFilter: StringControl;
  dateFilter: StringControl;
  timeFilter: StringControl;
  @observable
  availableOn: number | null;

  @observable
  currentPage: number;

  @observable.ref
  resultState: HttpPromiseState<PagedListResponse<User>> | null;

  @observable.ref
  selectUser: User | null;

  private readonly disposers = new Disposers();

  constructor(readonly parent: AdminClassroomSceneStore,
              readonly searchRole: ClassroomRole) {
    makeObservable(this);
    this.expanded = false;

    this.userFilter = new StringControl('', '');
    this.dateFilter = new StringControl('', '', [validators.strictDate]);
    this.timeFilter = new StringControl('', '', [validators.time]);
    this.availableOn = null;
    this.currentPage = 0;
    this.resultState = null;
    this.selectUser = null;

    this.setup();
    this.dump();
  }

  @action
  toggleExpand = () => {
    if (this.resultState === null)
      this.expanded = false;
    else
      this.expanded = !this.expanded;
  };

  @action
  searchUsers = () => {
    if (this.resultState?.isPending === true)
      return;

    const request: ClassroomUserSearchDto = {
      Filter: this.userFilter.value,
      Page: this.currentPage,
    };
    if (this.availableOn !== null)
      request.AvailableOn = this.availableOn;

    if (this.searchRole === 'Teacher')
      this.resultState = ClassroomLoaders.Admin.searchTeacher(request);
    if (this.searchRole === 'Student')
      this.resultState = ClassroomLoaders.Admin.searchStudent(request);
  };

  @action
  loadPreviousPage = () => {
    this.currentPage--;
    this.searchUsers();
  };

  @action
  loadNextPage = () => {
    this.currentPage++;
    this.searchUsers();
  };

  @action
  setSelectedUser(user: User | null) {
    this.selectUser = user;
  }

  @computed
  get classroomFilter(): ClassroomUserFilter {
    if (this.selectUser !== null)
      return {UserId: this.selectUser.Id};
    else
      return {Filter: this.userFilter.value};
  }

  private setup() {
    this.disposers.add(
      reaction(
        () => [this.dateFilter.value, this.timeFilter.value],
        this.updateAvailableOn),
      reaction(
        () => [this.userFilter.value, this.availableOn],
        this.onFilterChange),
      reaction(
        () => this.resultState?.value,
        this.onSearchResult)
    );
  }

  @action
  private updateAvailableOn = () => {
    this.availableOn = makeTime(this.dateFilter.value, this.timeFilter.value, currentTimeZone());
  };

  @action
  private onFilterChange = () => {
    this.currentPage = 0;
    this.resultState = null;
  };

  @action
  private onSearchResult = () => {
    if (this.resultState?.isResolved !== true)
      return;
    this.expanded = true;
    this.currentPage = this.resultState.value.CurrentPage;
  };

  dispose = () => {
    this.disposers.dispose();
  };

  dump() {
    reaction(
      () => [this.dateFilter.value, this.timeFilter.value, this.availableOn],
      () => {
        logger.log(
          'date', this.dateFilter.value,
          'time', this.timeFilter.value,
          'time', this.availableOn
        );
      });
  }
}
